/**
 * Session-scoped storage helpers for short-lived client values.
 * Falls back to localStorage only for backward compatibility reads.
 */

export const ANONYMOUS_SUBMISSION_ID_KEY = 'ae-anonymous-submission-id';

function canUseWebStorage(): boolean {
    return typeof window !== 'undefined';
}

function safeSessionStorage(): Storage | null {
    if (!canUseWebStorage()) return null;
    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
}

function safeLocalStorage(): Storage | null {
    if (!canUseWebStorage()) return null;
    try {
        return window.localStorage;
    } catch {
        return null;
    }
}

export function setTransientItem(key: string, value: string): void {
    const session = safeSessionStorage();
    if (session) {
        session.setItem(key, value);
        return;
    }

    const local = safeLocalStorage();
    if (local) {
        local.setItem(key, value);
    }
}

/**
 * Reads from sessionStorage first, then localStorage for migration compatibility.
 */
export function getTransientItem(key: string): string | null {
    const session = safeSessionStorage();
    const sessionValue = session?.getItem(key) ?? null;
    if (sessionValue !== null) return sessionValue;

    const local = safeLocalStorage();
    const localValue = local?.getItem(key) ?? null;
    if (localValue !== null && session) {
        session.setItem(key, localValue);
    }
    return localValue;
}

/**
 * Clears both session and local storage copies.
 */
export function removeTransientItem(key: string): void {
    safeSessionStorage()?.removeItem(key);
    safeLocalStorage()?.removeItem(key);
}
